import { CommandHistory } from "@/commands/CommandHistory";
import type { EditorSelectionState } from "@/commands/Command";
import {
  createBlankDocument,
  DEFAULT_DOCUMENT_VIEWPORT,
  type CurvaDocument,
  type DocumentViewport,
} from "@/document/model/document";
import { createDefaultEditorSelection } from "@/editor/store/selectionStore";
import type { EditorToolState } from "@/editor/store/editorStoreTypes";
import { createId } from "@/lib/ids";

export interface DocumentSession {
  id: string;
  document: CurvaDocument;
  history: CommandHistory;
  selection: EditorSelectionState;
  tool: EditorToolState;
  viewport: DocumentViewport;
  /** Absolute filesystem path when opened/saved via native dialogs (Phase 4+). */
  path?: string;
  /** `editorScripts` = special tab editing the global editor-scripts package. */
  kind?: "document" | "editorScripts";
}

/** Live editor projection parked into / hydrated from a session. */
export interface LiveEditorSnapshot {
  document: CurvaDocument;
  history: CommandHistory;
  selection: EditorSelectionState;
  tool: EditorToolState;
  viewport: DocumentViewport;
  path?: string;
  kind?: "document" | "editorScripts";
}

export function createDocumentSession(
  partial: Partial<Omit<DocumentSession, "id" | "history">> & {
    id?: string;
    history?: CommandHistory;
    document?: CurvaDocument;
  } = {},
): DocumentSession {
  const document = partial.document ?? createBlankDocument();
  const history = partial.history ?? new CommandHistory();
  history.markSaved(document.revision);
  return {
    id: partial.id ?? createId("session"),
    document,
    history,
    selection: partial.selection ?? createDefaultEditorSelection(),
    tool: partial.tool ?? { activeTool: "select", selectedShapeKind: "rectangle" },
    viewport: partial.viewport ?? { ...DEFAULT_DOCUMENT_VIEWPORT, ...document.viewport },
    path: partial.path,
    kind: partial.kind ?? "document",
  };
}

export function sessionFromLiveSnapshot(snapshot: LiveEditorSnapshot, id?: string): DocumentSession {
  return {
    id: id ?? createId("session"),
    document: snapshot.document,
    history: snapshot.history,
    selection: snapshot.selection,
    tool: snapshot.tool,
    viewport: snapshot.viewport,
    path: snapshot.path,
    kind: snapshot.kind ?? "document",
  };
}

export function liveSnapshotFromSession(session: DocumentSession): LiveEditorSnapshot {
  return {
    document: session.document,
    history: session.history,
    selection: session.selection,
    tool: session.tool,
    viewport: session.viewport,
    path: session.path,
    kind: session.kind ?? "document",
  };
}
